import React from 'react'

import { Chip, Stack, Typography, useTheme } from '@mui/material'
import { Switch } from '@repo/ui'

export interface PlanIntervalToggleProps {
  /** True = annual is currently shown. */
  checked: boolean
  onChange: (annual: boolean) => void
  /**
   * Text on the "Save X%" pill next to the toggle. `null` hides the pill.
   *
   * REQUIRED, and it has no default on purpose. It was `= 'Save 19%'` until
   * 2026-08-26, a figure no tier has ever saved (the four save 16.67% / 15.91%
   * / 16.42% / 16.59%), and a default is exactly what let a wrong price claim
   * reach both the public signup page and the billing page unnoticed. Pass
   * `planIntervalSavingsLabel(plans)` — which returns `null` while plans are
   * loading, so the pill is absent rather than guessing.
   */
  savingsLabel: string | null
}

/**
 * One end of the switch. The ACTIVE side carries the emphasis — 600 weight and
 * `text.primary` against 400 and `text.secondary` — which is the same pair the
 * signup stepper's own rail uses for the step you are on (`SignUpPage`, the
 * `STEP_LABEL` Typography). Until VERA-1095 both labels here were an identical
 * body3/400, so the only thing saying which interval was showing was the knob
 * position: a two-state control whose state is legible from one 20px dot, in a
 * row whose prices had already changed underneath it.
 *
 * The `::after` is a WIDTH RESERVATION, not decoration. Bold text is wider than
 * regular, so weighting on toggle would re-measure both labels and shove the
 * switch a couple of pixels sideways on every click — the flinch this page has
 * already removed twice elsewhere. The pseudo-element renders the same word at
 * 600 in a zero-height, hidden block, so the element is always as wide as its
 * bold form and nothing moves when the weight flips.
 */
const IntervalLabel = ({ label, active }: { label: string; active: boolean }) => (
  <Typography
    variant="body3"
    color={active ? 'text.primary' : 'text.secondary'}
    sx={{
      fontWeight: active ? 600 : 400,
      '&::after': {
        content: `"${label}"`,
        fontWeight: 600,
        display: 'block',
        height: 0,
        overflow: 'hidden',
        visibility: 'hidden',
      },
    }}
  >
    {label}
  </Typography>
)

/**
 * Monthly / Annual plan-interval switch + a "Save X%" pill. Purely
 * presentational — no auth/company state — so the public signup page can
 * render it as well as the authenticated billing page.
 *
 * Passed to `PlanComparisonMatrix`'s `intervalControl` slot, which puts it in
 * the label column's header cell: beside the prices it changes, rather than in
 * the page header far from them.
 *
 * VRG-383 noted that the billing plan-change page hand-rolled this same row
 * inline rather than importing it. It no longer does (2026-08-26): the two
 * copies each carried their own hardcoded savings figure, so the duplication
 * was not merely untidy — it was two places for the same wrong claim to hide.
 */
export const PlanIntervalToggle = ({
  checked,
  onChange,
  savingsLabel,
}: PlanIntervalToggleProps) => {
  const theme = useTheme()

  return (
    // `useFlexGap` + `flexWrap` so the "Save N%" pill drops to a second line
    // inside the matrix's ~220px label cell instead of spilling past it into
    // the first plan column. MUI's `spacing` does not survive a wrap without
    // `useFlexGap`.
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
    >
      <IntervalLabel label="Monthly" active={!checked} />
      <Switch checked={checked} onChange={() => onChange(!checked)} />
      <IntervalLabel label="Annual" active={checked} />
      {savingsLabel && (
        <Chip
          label={savingsLabel}
          sx={{
            p: 3.5,
            pl: 1,
            pr: 1,
            fontSize: 14,
            fontWeight: 500,
            // Mode-aware: the green scale isn't inverted for dark mode, so a
            // light green[100] pill read as an out-of-place light chip on the
            // dark page. Use a dark-green fill + light-green text in dark mode.
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.green[300]
                : theme.palette.green[700],
            border: `1px solid ${
              theme.palette.mode === 'dark'
                ? theme.palette.green[800]
                : theme.palette.green[200]
            }`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.green[950]
                : theme.palette.green[100],
          }}
        />
      )}
    </Stack>
  )
}
